// Register modal back-button interceptor BEFORE React Router initializes
import '@/hooks/useModalBackHandler';

import { App, ConfigProvider } from 'antd';
import jaJP from 'antd/locale/ja_JP';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { router } from './App';
import './scss/index.scss';
import './styles/datepicker-dist.scss';

/**
 * Application Entry Point
 *
 * Initializes the React application with multi-tenant support.
 * Uses RouterProvider with createBrowserRouter for improved routing performance.
 */

// Initialize module registry
import '@runtime/moduleInit';

import { queryClient } from '@shared/lib/react-query';
import { BottomBarProvider } from '@shared/providers/BottomBarContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { GlobalBehaviorWrapper } from './shared/providers/GlobalBehaviorWrapper';
import {
  activeSessionSyncWillLikelyRun,
  resolveActiveSessionSyncConfig,
} from './features/sso/activeSessionSync';
import { resolvePublicPortalProfile } from './features/sso/publicPortalProfile';
import {
  isSsoSite,
  normalizeUnifiedPortalStorage,
  readActivePortalAuthChannel,
} from './features/sso/ssoSession';
import { isCurtainlessPath, isUserPageReload, showSsoSyncCurtain } from './features/sso/ssoSyncCurtain';

// Purge split-channel credentials before React, providers, or API interceptors
// can observe them. This is idempotent and affects only env/config-agreed
// unified tenants.
const normalizeUnifiedStorageAtBoot = (): void => {
  const site = window.location.pathname.match(/^\/([^/]+)(?:\/|$)/)?.[1];
  if (!isSsoSite(site)) return;
  if (resolvePublicPortalProfile(site).mode !== 'unified') return;
  normalizeUnifiedPortalStorage(site, window.localStorage, window.sessionStorage);
};

// Raise the curtain BEFORE React renders, never after.
//
// Catching up with a session another Portal created costs a top-level round trip, and the trip was
// started from an effect -- which runs after the browser has already painted. The business screen
// appeared in full, then was torn down and rebuilt, and two paints for one arrival reads as the
// page glitching no matter how fast it is. The only fix is to never show the first paint, which
// means deciding here, in the entry module, while the DOM is still empty.
//
// Deliberately optimistic: this reads the cheap half of the sync decision (the cooldown, and the
// reload that overrides it). If the trigger then declines for a reason only it can see, it takes
// the curtain down and the app appears as usual.
const raiseCurtainIfSyncIsLikely = (): void => {
  try {
    // Never raise a curtain nobody can take down — see isCurtainlessPath. (HIT-11388)
    if (isCurtainlessPath(window.location.pathname)) {
      return;
    }

    const config = resolveActiveSessionSyncConfig(
      import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
      import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_COOLDOWN_MS,
    );
    if (config === undefined) {
      return;
    }

    const portal = window.location.pathname.match(/^\/([^/]+)(?:\/(admin))?(?:\/|$)/);
    const site = portal?.[1];
    if (site === undefined || !isSsoSite(site)) {
      return;
    }
    const sitePrefix = `/${site}`;
    const profile = resolvePublicPortalProfile(site);
    if (profile.mode === 'invalid') return;
    const unifiedPath = profile.mode === 'unified'
      && (window.location.pathname === profile.basePath
        || window.location.pathname.startsWith(`${profile.basePath}/`))
      && !['admin', 'massager', 'main', 'checkin'].some(segment =>
        window.location.pathname === `${sitePrefix}/${segment}`
        || window.location.pathname.startsWith(`${sitePrefix}/${segment}/`)
      );
    const entryType = unifiedPath
      ? 'unified'
      : portal?.[2] === 'admin' ? 'admin' : 'clinic';

    // No stored credential means nothing to reconcile: the login page must never sit behind a
    // curtain. In a unified tenant, principal type does not select another auth channel.
    const tokenKey = entryType === 'admin' ? 'site_admin_token' : 'auth_token';
    const activeChannel = readActivePortalAuthChannel(site, window.localStorage);
    if (window.localStorage.getItem(tokenKey) === null
      && !(activeChannel?.entryType === entryType && activeChannel.transport === 'cookie')) {
      return;
    }

    if (activeSessionSyncWillLikelyRun(
      { site, entryType },
      window.sessionStorage,
      config.cooldownMs,
      Date.now(),
      isUserPageReload(),
    )) {
      showSsoSyncCurtain();
    }
  } catch {
    // A curtain is a courtesy. Never let it stop the app from booting.
  }
};

normalizeUnifiedStorageAtBoot();
raiseCurtainIfSyncIsLikely();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ConfigProvider
      locale={jaJP}
      button={{ autoInsertSpace: false }}
      theme={{
        token: {
          fontFamily: '"Noto Sans JP", system-ui, sans-serif',
        },
      }}
    >
      <App>
        <GlobalBehaviorWrapper router={router}>
          <QueryClientProvider client={queryClient}>
            <BottomBarProvider>
              <RouterProvider router={router} />
            </BottomBarProvider>
          </QueryClientProvider>
        </GlobalBehaviorWrapper>
      </App>
    </ConfigProvider>
  </React.StrictMode>
);
